import { type KeyboardEvent, type ReactNode, useRef } from "react";

import { cn } from "../lib/cn";

export type typeSegmentedOption = {
  value: string;
  label: string;
};

export type typeSegmentedControlProps = {
  /** Names the group for screen readers. Not rendered visually. */
  label: string;
  options: readonly typeSegmentedOption[];
  /** The selected option. Keep it in a query param when it is a view state. */
  value: string;
  onValueChange: (value: string) => void;
  /** Stretch the track across its container. Off: the track hugs its labels. */
  fullWidth?: boolean | undefined;
  className?: string | undefined;
  testId?: string | undefined;
};

/**
 * A secondary switch inside one view: a team, a period, a standings grouping
 * (Scores UX spec v1.0 section 7.2). Never a page level tab row; that is
 * `Tabs`.
 *
 * Anatomy: a 36px `surface-sunken` track, radius full, and a solid
 * `primary-solid` pill with `on-primary` text that slides to the selected
 * segment over 250ms (`motion-slow`). Unselected labels are `text-secondary`.
 * The selection is the solid fill, never a pale tint (theme.mdc).
 *
 * Every segment is a 44px target: the root is 44 tall and the 36px track is
 * drawn 4px inside it, so the extra height is transparent padding on the
 * target rather than a taller control. The pill sits 4px inside the track.
 *
 * Keyboard: one tab stop for the group (roving tabindex), arrow keys move the
 * selection, Home and End jump, as a radio group does.
 *
 * Reduced motion: the pill jumps instead of sliding.
 */
export const SegmentedControl = ({
  label,
  options,
  value,
  onValueChange,
  fullWidth = false,
  className,
  testId,
}: typeSegmentedControlProps): ReactNode => {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const count = Math.max(options.length, 1);
  const foundIndex = options.findIndex((option) => option.value === value);
  const selectedIndex = foundIndex === -1 ? 0 : foundIndex;

  const select = (index: number): void => {
    const option = options[index];
    if (option === undefined) return;
    onValueChange(option.value);
    buttonRefs.current[index]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const last = options.length - 1;
    let next: number | null = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      next = selectedIndex === last ? 0 : selectedIndex + 1;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      next = selectedIndex === 0 ? last : selectedIndex - 1;
    } else if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = last;
    }
    if (next === null) return;
    event.preventDefault();
    select(next);
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      data-testid={testId}
      onKeyDown={onKeyDown}
      className={cn(
        "relative isolate grid min-h-touch-target px-1",
        fullWidth ? "w-full" : "w-fit max-w-full",
        className,
      )}
      style={{
        gridTemplateColumns: `repeat(${String(count)}, minmax(0, 1fr))`,
      }}
    >
      <span
        aria-hidden="true"
        data-segmented-track=""
        className="pointer-events-none absolute inset-x-0 inset-y-1 -z-10 rounded-full bg-surface-sunken"
      />
      {/* The pill rides a lane 4px inside the track, so its width is a plain
          share of the lane and its offset a whole number of its own widths. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-1 inset-y-2 -z-10"
      >
        <span
          data-segmented-indicator=""
          className={cn(
            "block h-full rounded-full bg-primary-solid",
            "transition-transform duration-300 ease-standard motion-reduce:transition-none",
          )}
          style={{
            width: `${String(100 / count)}%`,
            transform: `translateX(${String(selectedIndex * 100)}%)`,
          }}
        />
      </span>
      {options.map((option, index) => {
        const isSelected = index === selectedIndex;
        return (
          <button
            key={option.value}
            ref={(element) => {
              buttonRefs.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={isSelected ? 0 : -1}
            data-state={isSelected ? "checked" : "unchecked"}
            onClick={() => {
              if (!isSelected) onValueChange(option.value);
            }}
            className={cn(
              "flex min-h-touch-target min-w-0 items-center justify-center rounded-full px-4 text-caption",
              "transition-colors duration-200 ease-standard motion-reduce:transition-none",
              "focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-focus-ring",
              isSelected
                ? "text-on-primary"
                : "text-ink-secondary hover:text-ink",
            )}
          >
            <span className="truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};
